import NumberInput from '../NumberInput.jsx'
import InfoTooltip from '../InfoTooltip.jsx'
import { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ColorOpacityControl, { COLOR_SWATCHES } from './ColorOpacityControl.jsx'

// CE-04a — stateless presentational popover for indicator settings.
// CE-04b — generalized to render for any MA-shape indicator keyed by `kind`.
// CE-04c — refactored to descriptor-driven render via FIELD_SHAPE_BY_KIND.
//          Adds Bollinger Bands and Donchian Channels — the first multi-
//          component overlay indicators (3 lines + 1 background fill each).
//          The descriptor is the fourth member of the chassis lookup family,
//          alongside DEFAULT_PARAMS_BY_KIND / DEFAULT_STYLE_BY_KIND /
//          COMPUTE_BY_KIND. SMA + EMA produce the same popover output as
//          CE-04b (regression-guarded by the existing tests).
//
// Engine-blocked fields render disabled with the blocking ticket surfaced
// via the BlockedFieldTooltip wrapper. The descriptor's `blockedBy` field
// is a key into EI_TICKET_BY_FIELD, which remains the single source of
// truth for the EI ticket text.

/** Display name lookup keyed by `kind`. Shared with IndicatorAccordion. */
export const INDICATOR_DISPLAY_NAME = Object.freeze({
  sma: 'SMA',
  ema: 'EMA',
  keltner: 'Keltner Channel',
  bollinger: 'Bollinger Bands',
  donchian: 'Donchian Channels',
  volume: 'Volume',
  vwap_1d: 'VWAP 1D',
  vwap_2d: 'VWAP 2D',
  vwap_wtd: 'VWAP WTD',
  vwap_mtd: 'VWAP MTD',
  vwap_ytd: 'VWAP YTD',
  vwap_earnings: 'VWAP Earnings',
  vwap_earnings_2: 'VWAP 2 Earnings',
  pivots: 'Pivot Points',
  cars_v1: 'CA|RS',
})

export { COLOR_SWATCHES }

/** Engine-ticket map for greyed fields. Tooltip text on each disabled input. */
export const EI_TICKET_BY_FIELD = Object.freeze({
  source: 'EI-02',
  offset: 'EI-02',
  'smoothing.type': 'EI-03',
  'smoothing.length': 'EI-03',
  'smoothing.bb_std_dev': 'EI-03',
  ma_type: 'EI-03',
  timeframe: 'EI-04',
  wait_for_timeframe_closes: 'EI-05',
})

const SOURCE_OPTIONS = ['open', 'high', 'low', 'close', 'hl2', 'hlc3', 'ohlc4', 'hlcc4']
const KELTNER_BANDS_STYLE_OPTIONS = ['average_true_range', 'true_range', 'range']
const SMOOTHING_TYPES = ['None', 'SMA', 'EMA', 'WMA', 'RMA', 'HMA', 'SWMA', 'VWMA', 'BB']
const MA_TYPES = ['SMA', 'EMA', 'WMA', 'RMA']
const BAR_SIZE_OPTIONS = ['1 day', '30 mins', '15 mins', '5 mins']
const PANEL_CLASS = 'z-30 w-80 max-h-[28rem] overflow-auto rounded-md border border-slate-200 bg-white shadow-lg shadow-slate-900/15 text-slate-900 text-xs dark:border-white/10 dark:bg-[#0e1116] dark:text-white dark:shadow-black/30'
const TAB_BAR_CLASS = 'flex border-b border-slate-200 dark:border-white/10'
const TAB_ACTIVE_CLASS = 'bg-slate-100 text-slate-900 dark:bg-white/[0.04] dark:text-white'
const TAB_INACTIVE_CLASS = 'text-slate-500 hover:text-slate-900 dark:text-white/60 dark:hover:text-white'
const SECTION_CLASS = 'border-t border-slate-200 pt-2 first:border-t-0 first:pt-0 dark:border-white/[0.06]'
const LABEL_CLASS = 'block text-slate-500 mb-1 dark:text-white/60'
const DISABLED_LABEL_CLASS = 'block text-slate-400 mb-1 dark:text-white/40'
const SECTION_TITLE_CLASS = 'text-slate-700 font-medium dark:text-white/70'
const INLINE_MUTED_CLASS = 'flex items-center gap-1 text-slate-500 dark:text-white/60'
const CHECKBOX_LABEL_CLASS = 'flex items-center gap-2 text-slate-700 dark:text-white/70'
const CONTROL_CLASS = 'w-full px-2 py-1 rounded bg-white border border-slate-300 text-slate-900 focus:border-slate-500 outline-none dark:bg-white/[0.04] dark:border-white/10 dark:text-white dark:focus:border-white/30'
const DISABLED_CONTROL_CLASS = 'w-full px-2 py-1 text-xs rounded bg-slate-100 border border-slate-200 text-slate-400 cursor-not-allowed dark:bg-white/[0.02] dark:border-white/[0.06] dark:text-white/40'
const SEGMENT_ACTIVE_CLASS = 'border-slate-700 bg-slate-100 text-slate-900 dark:border-white dark:bg-white/[0.06] dark:text-white'
const SEGMENT_INACTIVE_CLASS = 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 dark:border-white/10 dark:bg-transparent dark:text-white dark:hover:border-white/30'
const FOOTER_CLASS = 'flex items-center justify-between border-t border-slate-200 px-3 py-2 dark:border-white/10'

const FIELD_LABELS = Object.freeze({
  length: 'Length',
  multiplier: 'Multiplier',
  std_mult: 'StdDev multiplier',
  source: 'Source',
  use_exponential_ma: 'Use Exponential MA',
  bands_style: 'Bands Style',
  atr_length: 'ATR Length',
  offset: 'Offset',
  'smoothing.type': 'Smoothing type',
  'smoothing.length': 'Smoothing length',
  'smoothing.bb_std_dev': 'Smoothing BB StdDev',
  ma_type: 'MA type',
  timeframe: 'Timeframe',
  wait_for_timeframe_closes: 'Wait for timeframe closes',
  color_based_on_previous_close: 'Color based on previous close',
  bar_size: 'Bar size',
})

// SMA + EMA share the same field shape per indicator-settings-spec.md §1+§2.
const MA_INPUTS = Object.freeze([
  { key: 'length', kind: 'int', live: true, min: 1 },
  { key: 'source', kind: 'enum', live: false, blockedBy: 'source', options: SOURCE_OPTIONS },
  { key: 'offset', kind: 'int', live: false, blockedBy: 'offset' },
  { key: 'smoothing.type', kind: 'enum', live: false, blockedBy: 'smoothing.type', options: SMOOTHING_TYPES },
  { key: 'smoothing.length', kind: 'int', live: false, blockedBy: 'smoothing.length', min: 1 },
  { key: 'smoothing.bb_std_dev', kind: 'float', live: false, blockedBy: 'smoothing.bb_std_dev', min: 0 },
  { key: 'timeframe', kind: 'timeframe', live: false, blockedBy: 'timeframe' },
  { key: 'wait_for_timeframe_closes', kind: 'bool', live: false, blockedBy: 'wait_for_timeframe_closes' },
])

const MA_OUTPUT_TOGGLES = Object.freeze([
  'labels_on_price_scale',
  'values_in_status_line',
  'inputs_in_status_line',
])

/**
 * Per-indicator field-shape descriptor. Single source of truth for which
 * controls render in the popover for any given indicator.
 *
 * - `inputs[]` drives the Inputs tab (one row per entry).
 * - `styleSections[]` drives the Style tab (one section per entry; `shape`
 *    selects line vs. fill controls).
 * - `outputToggles[]` drives the Style tab's bottom checkbox cluster.
 */
export const FIELD_SHAPE_BY_KIND = Object.freeze({
  sma: {
    inputs: MA_INPUTS,
    styleSections: [
      { key: 'ma', label: 'MA', shape: 'line' },
    ],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  ema: {
    inputs: MA_INPUTS,
    styleSections: [
      { key: 'ma', label: 'MA', shape: 'line' },
    ],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  keltner: {
    inputs: [
      { key: 'length', kind: 'int', live: true, min: 1 },
      { key: 'multiplier', kind: 'float', live: true, min: 0 },
      { key: 'source', kind: 'enum', live: true, options: SOURCE_OPTIONS },
      { key: 'use_exponential_ma', kind: 'bool', live: true },
      { key: 'bands_style', kind: 'enum', live: true, options: KELTNER_BANDS_STYLE_OPTIONS },
      { key: 'atr_length', kind: 'int', live: true, min: 1 },
      { key: 'timeframe', kind: 'timeframe', live: false, blockedBy: 'timeframe' },
      { key: 'wait_for_timeframe_closes', kind: 'bool', live: false, blockedBy: 'wait_for_timeframe_closes' },
    ],
    styleSections: [
      { key: 'basis', label: 'Middle', shape: 'line' },
      { key: 'upper', label: 'Upper', shape: 'line' },
      { key: 'lower', label: 'Lower', shape: 'line' },
      { key: 'background', label: 'Background', shape: 'fill' },
    ],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  bollinger: {
    inputs: [
      { key: 'length', kind: 'int', live: true, min: 1 },
      { key: 'std_mult', kind: 'float', live: true, min: 0 },
      { key: 'source', kind: 'enum', live: false, blockedBy: 'source', options: SOURCE_OPTIONS },
      { key: 'offset', kind: 'int', live: false, blockedBy: 'offset' },
      { key: 'ma_type', kind: 'enum', live: false, blockedBy: 'ma_type', options: MA_TYPES },
      { key: 'timeframe', kind: 'timeframe', live: false, blockedBy: 'timeframe' },
      { key: 'wait_for_timeframe_closes', kind: 'bool', live: false, blockedBy: 'wait_for_timeframe_closes' },
    ],
    styleSections: [
      { key: 'basis', label: 'Basis', shape: 'line' },
      { key: 'upper', label: 'Upper', shape: 'line' },
      { key: 'lower', label: 'Lower', shape: 'line' },
      { key: 'background', label: 'Background', shape: 'fill' },
    ],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  donchian: {
    inputs: [
      { key: 'length', kind: 'int', live: true, min: 1 },
      { key: 'offset', kind: 'int', live: false, blockedBy: 'offset' },
      { key: 'timeframe', kind: 'timeframe', live: false, blockedBy: 'timeframe' },
      { key: 'wait_for_timeframe_closes', kind: 'bool', live: false, blockedBy: 'wait_for_timeframe_closes' },
    ],
    styleSections: [
      { key: 'upper', label: 'Upper', shape: 'line' },
      { key: 'basis', label: 'Basis', shape: 'line' },
      { key: 'lower', label: 'Lower', shape: 'line' },
      { key: 'background', label: 'Background', shape: 'fill' },
    ],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  volume: {
    inputs: [
      { key: 'color_based_on_previous_close', kind: 'bool', live: true },
    ],
    styleSections: [
      { key: 'bars', label: 'Bars', shape: 'bars' },
    ],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  vwap_1d: {
    inputs: [],
    styleSections: [{ key: 'vwap', label: 'VWAP', shape: 'line' }],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  vwap_2d: {
    inputs: [],
    styleSections: [{ key: 'vwap', label: 'VWAP', shape: 'line' }],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  vwap_wtd: {
    inputs: [],
    styleSections: [{ key: 'vwap', label: 'VWAP', shape: 'line' }],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  vwap_mtd: {
    inputs: [],
    styleSections: [{ key: 'vwap', label: 'VWAP', shape: 'line' }],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  vwap_earnings: {
    inputs: [{ key: 'anchor_timing', label: 'Anchor timing', kind: 'enum', live: true, defaultValue: 'earnings-date',
      help: 'Earnings date’s open includes trading before an after-close release. After-release timing starts at the next regular open. The anchor follows the latest reported earnings. Daily and intraday charts need history back to the anchor.',
      options: ['earnings-date', 'after-release'], optionLabels: {
        'earnings-date': 'Earnings date’s open',
        'after-release': 'First regular-session open after release',
      } }],
    styleSections: [{ key: 'vwap', label: 'VWAP', shape: 'line' }],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  vwap_earnings_2: {
    inputs: [{ key: 'anchor_timing', label: 'Anchor timing', kind: 'enum', live: true, defaultValue: 'earnings-date',
      help: 'Earnings date’s open includes trading before an after-close release. After-release timing starts at the next regular open. The anchor follows the second-most-recent reported earnings. Daily and intraday charts need history back to the anchor.',
      options: ['earnings-date', 'after-release'], optionLabels: {
        'earnings-date': 'Earnings date’s open',
        'after-release': 'First regular-session open after release',
      } }],
    styleSections: [{ key: 'vwap', label: 'VWAP', shape: 'line' }],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  vwap_ytd: {
    inputs: [],
    styleSections: [{ key: 'vwap', label: 'VWAP', shape: 'line' }],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  pivots: {
    inputs: [
      { key: 'bar_size', kind: 'enum', live: true, options: BAR_SIZE_OPTIONS },
    ],
    styleSections: [
      { key: 'pivot', label: 'Pivot (PP)', shape: 'line' },
      { key: 'r1', label: 'R1', shape: 'line' },
      { key: 'r2', label: 'R2', shape: 'line' },
      { key: 'r3', label: 'R3', shape: 'line' },
      { key: 's1', label: 'S1', shape: 'line' },
      { key: 's2', label: 'S2', shape: 'line' },
      { key: 's3', label: 'S3', shape: 'line' },
    ],
    outputToggles: MA_OUTPUT_TOGGLES,
  },
  cars_v1: {
    inputs: [],
    styleSections: [
      { key: 'background', label: 'Tint', shape: 'cars_tint' },
    ],
    outputToggles: [],
    precision: false,
  },
})

const HEX_RE = /^#[0-9a-fA-F]{6}$/

function blockingTitle(field) {
  return `Blocked by ${EI_TICKET_BY_FIELD[field]} — engine support pending.`
}

function getNested(obj, path) {
  if (obj == null) return undefined
  const parts = path.split('.')
  let cursor = obj
  for (const p of parts) {
    if (cursor == null) return undefined
    cursor = cursor[p]
  }
  return cursor
}

/**
 * Custom CSS tooltip for engine-blocked fields. Replaces the native `title=`
 * mechanism (which fires after ~700ms of hover) with a near-instant Tailwind
 * group-hover tooltip (~150ms delay). Wraps the field's label + input so
 * hovering the label OR the input both trigger.
 *
 * @param {Object} props
 * @param {string} props.field
 * @param {'above'|'below'} [props.placement]
 * @param {React.ReactNode} props.children
 */
function BlockedFieldTooltip({ field, placement = 'above', children }) {
  const text = blockingTitle(field)
  const positionClasses = placement === 'below'
    ? 'top-full mt-1.5'
    : 'bottom-full mb-1.5'
  const arrowClasses = placement === 'below'
    ? 'bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-black'
    : 'top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-black'

  return (
    <div className="relative group">
      {children}
      <div
        data-testid={`popover-tooltip-${field}`}
        data-placement={placement}
        role="tooltip"
        className={`pointer-events-none absolute left-1/2 -translate-x-1/2 z-40 ${positionClasses} px-2 py-1 rounded bg-black border border-white/20 text-white text-[11px] whitespace-nowrap shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-100 delay-150`}
      >
        {text}
        <span className={`absolute ${arrowClasses}`} aria-hidden />
      </div>
    </div>
  )
}
BlockedFieldTooltip.propTypes = {
  field: PropTypes.string.isRequired,
  placement: PropTypes.oneOf(['above', 'below']),
  children: PropTypes.node.isRequired,
}

function GreyedInput({ field, value, type = 'text' }) {
  return (
    <input
      data-testid={`popover-input-${field}`}
      type={type}
      disabled
      readOnly
      value={value == null ? '' : String(value)}
      className={DISABLED_CONTROL_CLASS}
    />
  )
}
GreyedInput.propTypes = {
  field: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  type: PropTypes.string,
}

function GreyedSelect({ field, value }) {
  return (
    <select
      data-testid={`popover-input-${field}`}
      disabled
      value={value == null ? '' : String(value)}
      onChange={() => {}}
      className={DISABLED_CONTROL_CLASS}
    >
      <option value={value == null ? '' : String(value)}>
        {value == null ? '(none)' : String(value)}
      </option>
    </select>
  )
}
GreyedSelect.propTypes = {
  field: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
}

/**
 * Render a single Inputs-tab row for one descriptor entry.
 * Live entries dispatch onChange via `params.<entry.key>`; greyed entries
 * wrap their disabled control in <BlockedFieldTooltip>.
 */
function InputRow({ entry, params, onChange, isFirstGreyed }) {
  const label = entry.label || FIELD_LABELS[entry.key] || entry.key
  const value = getNested(params, entry.key) ?? entry.defaultValue

  // Live numeric input.
  if (entry.live && (entry.kind === 'int' || entry.kind === 'float')) {
    return (
      <div>
        <label className={LABEL_CLASS}>{label}</label>
        <NumberInput
          data-testid={`popover-input-${entry.key}`}
          min={entry.min}
          step={entry.kind === 'int' ? 1 : 'any'}
          value={value}
          onValueChange={number => onChange?.(`params.${entry.key}`, number)}
          className={CONTROL_CLASS}
        />
      </div>
    )
  }

  // Live enum dropdown (CE-04g-UI-b — Pivots' `bar_size` is chart-side
  // math, not engine-blocked, so it stays live-editable).
  if (entry.live && entry.kind === 'enum') {
    return (
      <div>
        <div className={LABEL_CLASS}>{label}{entry.help && <InfoTooltip text={entry.help} label={`${label} information`} placement="bottom" />}</div>
        <select
          data-testid={`popover-input-${entry.key}`}
          value={value == null ? '' : String(value)}
          onChange={(e) => onChange?.(`params.${entry.key}`, e.target.value)}
          className={CONTROL_CLASS}
        >
          {(entry.options || []).map((opt) => (
            <option key={opt} value={opt}>{entry.optionLabels?.[opt] || opt}</option>
          ))}
        </select>
      </div>
    )
  }

  // Live boolean toggle (CE-04c-2 — Volume's `color_based_on_previous_close`
  // is chart-side math, not engine-blocked, so it stays live-editable).
  if (entry.live && entry.kind === 'bool') {
    return (
      <label className={CHECKBOX_LABEL_CLASS}>
        <input
          data-testid={`popover-input-${entry.key}`}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange?.(`params.${entry.key}`, e.target.checked)}
        />
        <span>{label}</span>
      </label>
    )
  }

  // Greyed (engine-blocked) field.
  // Place the FIRST greyed field's tooltip BELOW so it does not clip outside
  // the popover's overflow-auto root — same hardening as CE-04a's source row.
  const placement = isFirstGreyed ? 'below' : 'above'
  let control
  if (entry.kind === 'enum') {
    control = <GreyedSelect field={entry.key} value={value} />
  } else if (entry.kind === 'bool') {
    control = <GreyedSelect field={entry.key} value={value} />
  } else if (entry.kind === 'timeframe') {
    control = <GreyedInput field={entry.key} value={value ?? ''} />
  } else if (entry.kind === 'int' || entry.kind === 'float') {
    control = <GreyedInput field={entry.key} type="number" value={value} />
  } else {
    control = <GreyedInput field={entry.key} value={value} />
  }

  return (
    <BlockedFieldTooltip field={entry.blockedBy || entry.key} placement={placement}>
      <label className={DISABLED_LABEL_CLASS}>{label}</label>
      {control}
    </BlockedFieldTooltip>
  )
}
InputRow.propTypes = {
  entry: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  isFirstGreyed: PropTypes.bool,
}

/**
 * Build a test-id suffix for per-section style controls. The legacy SMA/EMA
 * `ma` section emits unsuffixed test IDs (e.g. `popover-input-opacity`) so
 * existing CE-04a/b tests pass without modification. Multi-component
 * indicators (Bollinger, Donchian) suffix per section to keep IDs unique.
 */
function suffix(componentKey) {
  return componentKey === 'ma' ? '' : `-${componentKey}`
}

function mergeThemeStyle(base, themePatch) {
  if (!themePatch || typeof themePatch !== 'object' || Array.isArray(themePatch)) {
    return base || {}
  }
  const next = { ...(base || {}) }
  for (const [key, value] of Object.entries(themePatch)) {
    if (
      value
      && typeof value === 'object'
      && !Array.isArray(value)
      && next[key]
      && typeof next[key] === 'object'
      && !Array.isArray(next[key])
    ) {
      next[key] = mergeThemeStyle(next[key], value)
    } else {
      next[key] = value
    }
  }
  return next
}

function isThemeColorField(field) {
  return field === 'color'
    || field === 'opacity'
    || field.endsWith('.color')
    || field.endsWith('.opacity')
}

function StyleLineSection({ section, state, onChange }) {
  const sfx = suffix(section.key)

  return (
    <div className={SECTION_CLASS}>
      {section.label && (
        <div className="flex items-center justify-between mb-2">
          <span className={SECTION_TITLE_CLASS}>{section.label}</span>
          <label className={INLINE_MUTED_CLASS}>
            <input
              data-testid={`popover-toggle-visible${sfx}`}
              type="checkbox"
              checked={state?.visible !== false}
              onChange={(e) => onChange?.('visible', e.target.checked)}
            />
            <span>visible</span>
          </label>
        </div>
      )}
      <div>
        <label className={LABEL_CLASS}>Color</label>
        <ColorOpacityControl
          color={state?.color}
          opacity={state?.opacity ?? 100}
          onColorChange={(value) => onChange?.('color', value)}
          onOpacityChange={(value) => onChange?.('opacity', value)}
          triggerTestId={`popover-color-trigger${sfx}`}
          swatchTestIdPrefix={`popover-color-swatch${sfx}`}
          hexInputTestId={`popover-input-color-hex${sfx}`}
          opacityTestId={`popover-input-opacity${sfx}`}
          ariaLabel={`${section.label || 'Line'} color`}
        />
      </div>

      <div className="mt-3">
        <label className={LABEL_CLASS}>Thickness</label>
        <div className="flex gap-1">
          {[1, 2, 3, 4].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`popover-thickness-${n}${sfx}`}
              onClick={() => onChange?.('thickness', n)}
              className={`flex-1 px-2 py-1 rounded border ${
                state?.thickness === n
                  ? SEGMENT_ACTIVE_CLASS
                  : SEGMENT_INACTIVE_CLASS
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3">
        <label className={LABEL_CLASS}>Line style</label>
        <div className="flex gap-1">
          {['solid', 'dashed', 'dotted'].map((ls) => (
            <button
              key={ls}
              type="button"
              data-testid={`popover-line-style-${ls}${sfx}`}
              onClick={() => onChange?.('line_style', ls)}
              className={`flex-1 px-2 py-1 rounded border capitalize ${
                state?.line_style === ls
                  ? SEGMENT_ACTIVE_CLASS
                  : SEGMENT_INACTIVE_CLASS
              }`}
            >
              {ls}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
StyleLineSection.propTypes = {
  section: PropTypes.object.isRequired,
  state: PropTypes.object,
  onChange: PropTypes.func.isRequired,
}

function StyleFillSection({ section, state, onChange }) {
  const sfx = suffix(section.key)

  return (
    <div className={SECTION_CLASS}>
      <div className="flex items-center justify-between mb-2">
        <span className={SECTION_TITLE_CLASS}>{section.label}</span>
        <label className={INLINE_MUTED_CLASS}>
          <input
            data-testid={`popover-toggle-visible${sfx}`}
            type="checkbox"
            checked={state?.visible !== false}
            onChange={(e) => onChange?.('visible', e.target.checked)}
          />
          <span>visible</span>
        </label>
      </div>
      <div>
        <label className={LABEL_CLASS}>Color</label>
        <ColorOpacityControl
          color={state?.color}
          opacity={state?.opacity ?? 100}
          onColorChange={(value) => onChange?.('color', value)}
          onOpacityChange={(value) => onChange?.('opacity', value)}
          triggerTestId={`popover-color-trigger${sfx}`}
          swatchTestIdPrefix={`popover-color-swatch${sfx}`}
          hexInputTestId={`popover-input-color-hex${sfx}`}
          opacityTestId={`popover-input-opacity${sfx}`}
          ariaLabel={`${section.label} color`}
        />
      </div>
    </div>
  )
}
StyleFillSection.propTypes = {
  section: PropTypes.object.isRequired,
  state: PropTypes.object,
  onChange: PropTypes.func.isRequired,
}

/**
 * CE-04c-2 — Style section for Volume bars (the `shape: 'bars'` descriptor
 * entry). Renders visible checkbox + growing color picker + falling color
 * picker. No opacity / thickness / line_style controls — bars-shape is
 * limited to visibility + two colors per indicator-settings-spec.md §5.
 *
 * Test-id sub-suffix scheme: `-bars-growing` / `-bars-falling`. The section
 * key is `bars`, suffixed via the shared `suffix()` helper; the
 * growing/falling sub-suffix distinguishes the two color pickers.
 */
function StyleBarsSection({ section, state, onChange }) {
  const sfx = suffix(section.key)

  return (
    <div className={SECTION_CLASS}>
      <div className="flex items-center justify-between mb-2">
        <span className={SECTION_TITLE_CLASS}>{section.label}</span>
        <label className={INLINE_MUTED_CLASS}>
          <input
            data-testid={`popover-toggle-visible${sfx}`}
            type="checkbox"
            checked={state?.visible !== false}
            onChange={(e) => onChange?.('visible', e.target.checked)}
          />
          <span>visible</span>
        </label>
      </div>

      <div>
        <label className={LABEL_CLASS}>Growing color</label>
        <ColorOpacityControl
          color={state?.growing?.color}
          opacity={state?.growing?.opacity ?? 100}
          onColorChange={(value) => onChange?.('growing.color', value)}
          onOpacityChange={(value) => onChange?.('growing.opacity', value)}
          triggerTestId={`popover-color-trigger${sfx}-growing`}
          swatchTestIdPrefix={`popover-color-swatch${sfx}-growing`}
          hexInputTestId={`popover-input-color-hex${sfx}-growing`}
          opacityTestId={`popover-input-opacity${sfx}-growing`}
          ariaLabel="Growing color"
        />
      </div>

      <div className="mt-3">
        <label className={LABEL_CLASS}>Falling color</label>
        <ColorOpacityControl
          color={state?.falling?.color}
          opacity={state?.falling?.opacity ?? 100}
          onColorChange={(value) => onChange?.('falling.color', value)}
          onOpacityChange={(value) => onChange?.('falling.opacity', value)}
          triggerTestId={`popover-color-trigger${sfx}-falling`}
          swatchTestIdPrefix={`popover-color-swatch${sfx}-falling`}
          hexInputTestId={`popover-input-color-hex${sfx}-falling`}
          opacityTestId={`popover-input-opacity${sfx}-falling`}
          ariaLabel="Falling color"
        />
      </div>
    </div>
  )
}
StyleBarsSection.propTypes = {
  section: PropTypes.object.isRequired,
  state: PropTypes.object,
  onChange: PropTypes.func.isRequired,
}

function StyleCarsTintSection({ section, state, onChange }) {
  const [hexDraft, setHexDraft] = useState(state?.color || '')

  useEffect(() => {
    setHexDraft(state?.color || '')
  }, [state?.color])

  function handleHexChange(e) {
    const v = e.target.value
    setHexDraft(v)
    if (HEX_RE.test(v)) onChange?.('color', v)
  }

  return (
    <div className={SECTION_CLASS}>
      {section.label && (
        <div className="flex items-center justify-between mb-2">
          <span className={SECTION_TITLE_CLASS}>{section.label}</span>
        </div>
      )}
      <div className="grid grid-cols-[1fr_4rem] gap-2">
        <div>
          <label className={LABEL_CLASS}>Color</label>
          <input
            data-testid="popover-input-color-background"
            value={hexDraft}
            onChange={handleHexChange}
            className={`${CONTROL_CLASS} font-mono`}
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>Opacity</label>
          <NumberInput
            data-testid="popover-input-opacity-background"
            min="0"
            max="100"
            value={state?.opacity ?? 12}
            onValueChange={number => onChange?.('opacity', number)}
            className={CONTROL_CLASS}
          />
        </div>
      </div>
    </div>
  )
}

StyleCarsTintSection.propTypes = {
  section: PropTypes.object.isRequired,
  state: PropTypes.object,
  onChange: PropTypes.func.isRequired,
}

const KIND_ENUM = [
  'sma', 'ema', 'keltner', 'bollinger', 'donchian', 'volume',
  'vwap_1d', 'vwap_2d', 'vwap_wtd', 'vwap_mtd', 'vwap_ytd', 'vwap_earnings', 'vwap_earnings_2',
  'pivots', 'cars_v1',
]

/**
 * @param {Object} props
 * @param {'sma'|'ema'|'keltner'|'bollinger'|'donchian'|'volume'} props.kind
 * @param {Object} props.params
 * @param {Object} props.style
 * @param {(field: string, value: any) => void} props.onChange
 * @param {() => void} props.onRemove
 * @param {() => void} props.onClose
 * @param {{ top?: number|string, left?: number|string }} [props.anchorPos]
 */
export default function IndicatorPopover({
  kind,
  params,
  style,
  theme = null,
  onChange,
  onRemove,
  onClose,
  anchorPos = null,
}) {
  const [tab, setTab] = useState('inputs')
  const rootRef = useRef(null)

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose?.()
    }
    function onMouseDown(e) {
      if (e.target?.closest?.('[data-color-opacity-popover="true"]')) return
      if (rootRef.current && !rootRef.current.contains(e.target)) onClose?.()
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onMouseDown)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onMouseDown)
    }
  }, [onClose])

  const descriptor = FIELD_SHAPE_BY_KIND[kind] || FIELD_SHAPE_BY_KIND.sma
  const displayName = INDICATOR_DISPLAY_NAME[kind] || (typeof kind === 'string' ? kind.toUpperCase() : 'Indicator')

  // Index of the first greyed entry → its tooltip flips to `placement='below'`
  // so it does not clip outside the popover's overflow-auto root (carried
  // forward from CE-04a's source-row hardening).
  const firstGreyedIndex = useMemo(() => {
    return descriptor.inputs.findIndex((e) => !e.live)
  }, [descriptor])

  const positionStyle = anchorPos
    ? { position: 'absolute', top: anchorPos.top, left: anchorPos.left }
    : { position: 'absolute', top: 36, left: 8 }

  return (
    <div
      ref={rootRef}
      data-testid="indicator-popover"
      style={positionStyle}
      className={PANEL_CLASS}
      role="dialog"
      aria-label={`${displayName} settings`}
    >
      {/* Tabs */}
      <div className={TAB_BAR_CLASS}>
        <button
          type="button"
          data-testid="popover-tab-inputs"
          data-active={String(tab === 'inputs')}
          onClick={() => setTab('inputs')}
          className={`flex-1 px-3 py-2 ${tab === 'inputs' ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS}`}
        >
          Inputs
        </button>
        <button
          type="button"
          data-testid="popover-tab-style"
          data-active={String(tab === 'style')}
          onClick={() => setTab('style')}
          className={`flex-1 px-3 py-2 ${tab === 'style' ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS}`}
        >
          Style
        </button>
      </div>

      {/* Body */}
      {tab === 'inputs' ? (
        <div className="p-3 space-y-3">
          {descriptor.inputs.map((entry, idx) => (
            <InputRow
              key={entry.key}
              entry={entry}
              params={params}
              onChange={onChange}
              isFirstGreyed={idx === firstGreyedIndex}
            />
          ))}
        </div>
      ) : (
        <div className="p-3 space-y-3">
          {descriptor.styleSections.map((section) => {
            const sectionState = mergeThemeStyle(
              style?.[section.key],
              style?.themeStyles?.[theme]?.[section.key]
            )
            const handleSectionChange = (field, value) => {
              const prefix = theme && isThemeColorField(field)
                ? `style.themeStyles.${theme}.${section.key}`
                : `style.${section.key}`
              onChange?.(`${prefix}.${field}`, value)
            }
            if (section.shape === 'fill') {
              return (
                <StyleFillSection
                  key={section.key}
                  section={section}
                  state={sectionState}
                  onChange={handleSectionChange}
                />
              )
            }
            if (section.shape === 'bars') {
              return (
                <StyleBarsSection
                  key={section.key}
                  section={section}
                  state={sectionState}
                  onChange={handleSectionChange}
                />
              )
            }
            if (section.shape === 'cars_tint') {
              return (
                <StyleCarsTintSection
                  key={section.key}
                  section={section}
                  state={sectionState}
                  onChange={handleSectionChange}
                />
              )
            }
            return (
              <StyleLineSection
                key={section.key}
                section={section}
                state={sectionState}
                onChange={handleSectionChange}
              />
            )
          })}

          {descriptor.precision !== false && (
            <div className={SECTION_CLASS}>
              <label className={LABEL_CLASS}>Precision</label>
              <select
                data-testid="popover-input-precision"
                defaultValue={style?.precision ?? 'default'}
                onChange={(e) => {
                  const v = e.target.value
                  onChange?.('style.precision', v === 'default' ? 'default' : Number(v))
                }}
                className={CONTROL_CLASS}
              >
                <option value="default">default</option>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          )}

          {/* CE-04g-UI-pivots-rth-and-style-fixes — pivots-only Font size
              control. Lives in the Style tab (writes via `style.font_size`)
              rather than the Inputs tab so it doesn't need a new
              InputRow `path` semantics escape hatch. */}
          {kind === 'pivots' && (
            <div className={SECTION_CLASS}>
              <label className={LABEL_CLASS}>Font size</label>
              <select
                data-testid="popover-input-font-size"
                value={style?.font_size ?? 11}
                onChange={(e) => onChange?.('style.font_size', Number(e.target.value))}
                className={CONTROL_CLASS}
              >
                {[9, 10, 11, 12, 13].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-1">
            {descriptor.outputToggles.map((f) => (
              <label key={f} className={CHECKBOX_LABEL_CLASS}>
                <input
                  data-testid={`popover-toggle-${f}`}
                  type="checkbox"
                  checked={!!style?.[f]}
                  onChange={(e) => onChange?.(`style.${f}`, e.target.checked)}
                />
                <span className="capitalize">{f.replace(/_/g, ' ')}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className={FOOTER_CLASS}>
        <button
          type="button"
          data-testid="popover-remove"
          onClick={() => onRemove?.()}
          className="text-rose-600 hover:text-rose-500 dark:text-rose-400 dark:hover:text-rose-300"
        >
          Remove indicator
        </button>
        <button
          type="button"
          data-testid="popover-close"
          onClick={() => onClose?.()}
          className="px-2 py-1 rounded border border-slate-200 hover:border-slate-400 dark:border-white/10 dark:hover:border-white/30"
        >
          Close
        </button>
      </div>
    </div>
  )
}

IndicatorPopover.propTypes = {
  kind: PropTypes.oneOf(KIND_ENUM).isRequired,
  params: PropTypes.object.isRequired,
  style: PropTypes.object.isRequired,
  theme: PropTypes.oneOf(['dark', 'light']),
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  anchorPos: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    left: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
}
